import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { RefreshIcon } from "@aureskonnect/react-ui";

import {
    designStore,
    generalConfigStore,
    setCompositeProductDisplay,
} from "@store";
import type { CompositeProductDisplayPatch } from "@store";

import {
    CATEGORY_PRIMARY_SENTINEL,
    CATEGORY_SECONDARY_SENTINEL,
    CATEGORY_OPACITY_MAX,
    CATEGORY_BORDER_STYLE_NONE,
    CATEGORY_DISPLAY_MODE_1,
    CATEGORY_DISPLAY_MODE_2,
} from "@constants/index";

import {
    DisplayStyleSettings,
    DisplayStyleSetters,
    DisplayStyleValues,
} from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/DisplayStyleSettings";
import {
    makeResolveColor,
    DisplayDesignColors,
} from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/displayColor";
import { persistCompositeProductPatch } from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/persistCompositeProduct";
import {
    COMPOSITE_PRODUCT_MODE_PERSIST_KEY,
    COMPOSITE_PRODUCT_MODE1_PERSIST_KEYS,
    COMPOSITE_PRODUCT_MODE2_PERSIST_KEYS,
    getCompositePersistKeys,
    getInheritedProductDisplay,
    getEffectiveCompositeProductDisplay,
    getEffectiveCompositeProductMode,
} from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/compositeProductDisplay";

// Réglages d'affichage produit de l'ÉTAPE produit composé (ESS-3857).
// Par défaut : hérite des réglages produit globaux (mode actif). Toute édition
// active l'override local. Le « Mode d'affichage Produit » (Mode 1/Mode 2) est
// PROPRE à l'étape et possède SON PROPRE jeu de valeurs par mode (indépendant,
// comme l'interface générique globale) : basculer le mode édite le bon jeu.
// À l'activation de l'override, on sème les DEUX jeux depuis le global.
// « Réinitialiser » restaure l'héritage (override off) et efface les deux jeux,
// sans toucher au global.

// Champs store (clés de CompositeProductDisplayPatch) d'un jeu override, indexés
// par champ de style. Un seul setter de patch écrit le store : on bâtit ici le
// mapping style → clé store pour chaque mode.
type ModeStoreKeys = Record<
    keyof DisplayStyleValues,
    keyof CompositeProductDisplayPatch
>;

// Jeu Mode 2 (historique, sans suffixe) et jeu Mode 1 (suffixe `Mode1`).
const MODE2_STORE_KEYS: ModeStoreKeys = {
    backgroundColor: "compositeProductDisplayBackgroundColor",
    backgroundOpacity: "compositeProductDisplayBackgroundOpacity",
    selectedTextColor: "compositeProductDisplaySelectedTextColor",
    borderColor: "compositeProductDisplayBorderColor",
    borderStyle: "compositeProductDisplayBorderStyle",
    svgBorder: "compositeProductDisplaySvgBorder",
    svgBorderName: "compositeProductDisplaySvgBorderName",
};
const MODE1_STORE_KEYS: ModeStoreKeys = {
    backgroundColor: "compositeProductDisplayMode1BackgroundColor",
    backgroundOpacity: "compositeProductDisplayMode1BackgroundOpacity",
    selectedTextColor: "compositeProductDisplayMode1SelectedTextColor",
    borderColor: "compositeProductDisplayMode1BorderColor",
    borderStyle: "compositeProductDisplayMode1BorderStyle",
    svgBorder: "compositeProductDisplayMode1SvgBorder",
    svgBorderName: "compositeProductDisplayMode1SvgBorderName",
};

// Bâtit le patch store d'un jeu complet (7 champs) sous les clés du mode donné.
function jeuStorePatch(
    keys: ModeStoreKeys,
    v: DisplayStyleValues
): CompositeProductDisplayPatch {
    return {
        [keys.backgroundColor]: v.backgroundColor,
        [keys.backgroundOpacity]: v.backgroundOpacity,
        [keys.selectedTextColor]: v.selectedTextColor,
        [keys.borderColor]: v.borderColor,
        [keys.borderStyle]: v.borderStyle,
        [keys.svgBorder]: v.svgBorder,
        [keys.svgBorderName]: v.svgBorderName,
    };
}

export function CompositeProductDisplaySettings(): JSX.Element {
    const { t } = useTranslation();
    const {
        project: { template },
    } = useSnapshot(generalConfigStore);
    const design = useSnapshot(designStore);

    // `template.content` est typé via un index signature union (ProjectContentType) :
    // la slot `generalDesign` peut donc être absente (template d'un autre type de
    // kiosk). On annote vers la forme minimale réellement consommée
    // (DisplayDesignColors), nullable : makeResolveColor garde déjà chaque accès
    // via `?.` et applique ses fallbacks si la slot manque — pas de crash.
    const resolveColor = makeResolveColor(
        template.content.generalDesign as DisplayDesignColors | undefined
    );

    const isOverride = design.compositeProductDisplayOverride;
    const effective = getEffectiveCompositeProductDisplay(design);
    const effectiveMode = getEffectiveCompositeProductMode(design);
    const activeKeys = getCompositePersistKeys(effectiveMode);

    // Clés store du jeu du mode actif (les setters écrivent un patch unique).
    const activeStoreKeys =
        effectiveMode === CATEGORY_DISPLAY_MODE_1
            ? MODE1_STORE_KEYS
            : MODE2_STORE_KEYS;

    // Active l'override s'il ne l'est pas déjà, en semant LES DEUX jeux (Mode 1 +
    // Mode 2) depuis les valeurs globales du mode correspondant. Renvoie le patch
    // de seed (clés template) à fusionner dans la persistance.
    // Garde sur le store LIVE (et non le snapshot de rendu) : un upload SVG
    // appelle deux setters d'affilée — sans ça, le 2e re-sèmerait et écraserait.
    function ensureOverrideSeed(): Record<string, unknown> {
        if (designStore.compositeProductDisplayOverride) {
            return {};
        }
        const seedMode1 = getInheritedProductDisplay(
            design,
            CATEGORY_DISPLAY_MODE_1
        );
        const seedMode2 = getInheritedProductDisplay(
            design,
            CATEGORY_DISPLAY_MODE_2
        );
        // Un seul patch store : override + les deux jeux semés.
        setCompositeProductDisplay({
            compositeProductDisplayOverride: true,
            ...jeuStorePatch(MODE1_STORE_KEYS, seedMode1),
            ...jeuStorePatch(MODE2_STORE_KEYS, seedMode2),
        });
        return {
            compositeProductDisplayOverride: true,
            ...jeuPatch(COMPOSITE_PRODUCT_MODE1_PERSIST_KEYS, seedMode1),
            ...jeuPatch(COMPOSITE_PRODUCT_MODE2_PERSIST_KEYS, seedMode2),
        };
    }

    // Patch de persistance d'un jeu complet sous les clés template du mode donné.
    function jeuPatch(
        keys: Record<keyof DisplayStyleValues, string>,
        v: DisplayStyleValues
    ): Record<string, unknown> {
        return {
            [keys.backgroundColor]: v.backgroundColor,
            [keys.backgroundOpacity]: v.backgroundOpacity,
            [keys.selectedTextColor]: v.selectedTextColor,
            [keys.borderColor]: v.borderColor,
            [keys.borderStyle]: v.borderStyle,
            [keys.svgBorder]: v.svgBorder,
            [keys.svgBorderName]: v.svgBorderName,
        };
    }

    // Fabrique un setter d'affichage composite pour le JEU DU MODE ACTIF :
    // (1) garantit le seed override, (2) écrit le champ dans le bon jeu store via
    // le patch unique, (3) persiste sous les clés du mode actif (seed inclus).
    function makeCompositeSetter<V>(
        styleKey: keyof DisplayStyleValues
    ): (value: V) => void {
        return (value: V) => {
            const seed = ensureOverrideSeed();
            setCompositeProductDisplay({
                [activeStoreKeys[styleKey]]: value,
            } as CompositeProductDisplayPatch);
            persistCompositeProductPatch({
                ...seed,
                [activeKeys[styleKey]]: value,
            });
        };
    }

    const setters: DisplayStyleSetters = {
        setBackgroundColor: makeCompositeSetter("backgroundColor"),
        setBackgroundOpacity: makeCompositeSetter("backgroundOpacity"),
        setSelectedTextColor: makeCompositeSetter("selectedTextColor"),
        setBorderColor: makeCompositeSetter("borderColor"),
        setBorderStyle: makeCompositeSetter("borderStyle"),
        setSvgBorder: makeCompositeSetter("svgBorder"),
        setSvgBorderName: makeCompositeSetter("svgBorderName"),
    };

    // « Mode d'affichage Produit » : PROPRE à l'étape, indépendant du mode global.
    // Changer le mode active l'override (seed des deux jeux) si besoin, puis pose
    // le mode local. Les couleurs affichées suivent alors le jeu de ce mode (donc
    // Mode 1 et Mode 2 diffèrent une fois l'override actif). Persistance composite.
    function handleModeChange(mode: string): void {
        const seed = ensureOverrideSeed();
        setCompositeProductDisplay({ compositeProductDisplayMode: mode });
        persistCompositeProductPatch({
            ...seed,
            [COMPOSITE_PRODUCT_MODE_PERSIST_KEY]: mode,
        });
    }

    // « Réinitialiser » : restaure l'héritage des réglages globaux (override off)
    // et efface les DEUX jeux override locaux. N'altère PAS la config globale.
    function handleReset(): void {
        const blank: DisplayStyleValues = {
            backgroundColor: CATEGORY_PRIMARY_SENTINEL,
            backgroundOpacity: CATEGORY_OPACITY_MAX,
            selectedTextColor: CATEGORY_SECONDARY_SENTINEL,
            borderColor: CATEGORY_PRIMARY_SENTINEL,
            borderStyle: CATEGORY_BORDER_STYLE_NONE,
            svgBorder: "",
            svgBorderName: "",
        };
        // Un seul patch store : override off + mode par défaut + les deux jeux
        // remis à blanc.
        setCompositeProductDisplay({
            compositeProductDisplayOverride: false,
            compositeProductDisplayMode: CATEGORY_DISPLAY_MODE_1,
            ...jeuStorePatch(MODE1_STORE_KEYS, blank),
            ...jeuStorePatch(MODE2_STORE_KEYS, blank),
        });
        persistCompositeProductPatch({
            compositeProductDisplayOverride: false,
            [COMPOSITE_PRODUCT_MODE_PERSIST_KEY]: CATEGORY_DISPLAY_MODE_1,
            ...jeuPatch(COMPOSITE_PRODUCT_MODE1_PERSIST_KEYS, blank),
            ...jeuPatch(COMPOSITE_PRODUCT_MODE2_PERSIST_KEYS, blank),
        });
    }

    return (
        <div
            className="d-flex flex-column justify-content-between mx-2 m-2 rounded border"
            data-testid="composite-product-display-section"
        >
            <div
                className="text-left px-4 d-flex align-items-center justify-content-between cursor__clz"
                style={{
                    backgroundColor: "#EDEDED",
                    minHeight: "40px",
                    gap: "12px",
                }}
            >
                <span style={{ color: "#000000" }}>
                    <span style={{ fontSize: "20px" }}>
                        {t("Product display")}
                    </span>
                    <span style={{ fontSize: "13px" }}>
                        {" "}
                        {t(
                            "Warning: this setting can be overwritten by the product configuration in the generic interface"
                        )}
                    </span>
                </span>
                <button
                    type="button"
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        border: "1px solid #CECECE",
                        borderRadius: "4px",
                        background: "#FFFFFF",
                        cursor: isOverride ? "pointer" : "not-allowed",
                        padding: "4px",
                        flexShrink: 0,
                    }}
                    disabled={!isOverride}
                    onClick={handleReset}
                    data-testid="composite-product-display-reset-btn"
                    aria-label={t("Reset")}
                    title={t("Reset")}
                >
                    <RefreshIcon height={15} width={15} />
                </button>
            </div>
            <div className="p-4">
                <DisplayStyleSettings
                        testIdPrefix="composite-product"
                        backgroundColorLabel={t("Product background color")}
                        textColorLabel={t(
                            "Product text color (title, description, price)"
                        )}
                        svgFrameTitle={t("Item frame")}
                        svgFrameSubtitle={t(
                            "Insert an SVG frame for the selected item"
                        )}
                        svgFrameTitleBold={false}
                        modeSelector={{
                            label: t("Product display mode"),
                            mode1Label: t("Mode 1"),
                            mode2Label: t("Mode 2"),
                            mode1Value: CATEGORY_DISPLAY_MODE_1,
                            mode2Value: CATEGORY_DISPLAY_MODE_2,
                            currentMode: effectiveMode,
                            onChange: handleModeChange,
                            testIdPrefix: "composite-product-display-mode",
                        }}
                        persistKeys={activeKeys}
                        resolveColor={resolveColor}
                        // persistance gérée par les setters wrappés ci-dessus
                        // (ils doivent inclure le seed override) → prop persist
                        // neutralisée pour éviter une double persistance partielle.
                        persist={() => undefined}
                        values={effective}
                        setters={setters}
                    />
            </div>
        </div>
    );
}
